import {appLocalStorage} from '../services';

const buildCustomAttributes = (address) => {
  const attributes = Array.isArray(address['custom_attributes'])
    ? [...address['custom_attributes']]
    : [];

  const cocNumber = address['coc_number']
    || address?.extension_attributes?.coc_number
    || '';

  const withoutCoc = attributes.filter((attribute) => {
    if (!attribute || typeof attribute !== 'object') {
      return true;
    }
    return attribute.attribute_code !== 'coc_number';
  });

  if (cocNumber) {
    withoutCoc.push({
      attribute_code: 'coc_number',
      value: cocNumber,
    });
  }

  return withoutCoc;
};

const buildExtensionAttributes = (address) => {
  const extensionAttributes = {
    ...(address['extension_attributes'] || {}),
  };

  const cocNumber = address['coc_number']
    || address?.extension_attributes?.coc_number
    || '';

  if (cocNumber) {
    extensionAttributes.coc_number = cocNumber;
  } else {
    delete extensionAttributes.coc_number;
  }

  return extensionAttributes;
};

const toCustomerAddressId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const collectCustomerAddressId = (address) => {
  if (!address || typeof address !== 'object' || address['newAddress']) {
    return null;
  }

  return toCustomerAddressId(
    address['id']
      ?? address['entity_id']
      ?? address['customerAddressId']
      ?? address['customer_address_id']
  );
};

// Magento guest quotes reject leftover customer_address_id values from a
// previous logged-in session ("Invalid customer address id").
const stripCustomerAddressIdentity = (address) => {
  if (!address || typeof address !== 'object') {
    return address || {};
  }

  const next = {...address};
  delete next.id;
  delete next.entity_id;
  delete next.customerAddressId;
  delete next.customer_address_id;

  return next;
};

const sanitizePersistedAddress = (address, allowedIds) => {
  const id = collectCustomerAddressId(address);
  if (id && allowedIds.includes(id)) {
    return address;
  }

  return stripCustomerAddressIdentity(address);
};

const mapAddress = (address, allowedIds = []) => {
  address = address || {};
  const customerAddressId = collectCustomerAddressId(address);
  const allowedCustomerAddressId = customerAddressId && allowedIds.includes(customerAddressId)
    ? customerAddressId
    : null;

  return {
    city: address['city'] || '',
    company: address['company'] || '',
    country_id: address['country_code'] || address['country_id'] || '',
    customerAddressId: allowedCustomerAddressId,
    firstname: address['firstname'] || '',
    middlename: address['middlename'] || '',
    lastname: address['lastname'] || '',
    postcode: address['postcode'] || '',
    prefix: address['prefix'] || '',
    vat_id: address['vat_id'] || null,
    region: null,
    regionCode: null,
    saveInAddressBook: address['newAddress'] ? 1 : null,
    street: 'string' === typeof address['street'] ? address['street'].split(/\n/) : address['street'] || ['', '', ''],
    telephone: address['telephone'],
    email: address['email'] || appLocalStorage.getItem('checkout-customer-email', '') || '',
    extension_attributes: buildExtensionAttributes(address),
    custom_attributes: buildCustomAttributes(address),
  };
};

const initialState = () => {
    return {
        billingAddress: appLocalStorage.getItem('checkout-billing-address', {}),
        shippingAddress: appLocalStorage.getItem('checkout-shipping-address', {}),
        billingIsSameAsShipping: appLocalStorage.getItem('checkout-billing-is-same-as-shipping', true),
        isLoggedIn: false,
        customerAddressIds: [],
    };
};

const getters = {
    billingAddress: (state) => (state.billingIsSameAsShipping ? state.shippingAddress : state.billingAddress),
    shippingAddress: (state) => state.shippingAddress,
    billingIsSameAsShipping: (state) => state.billingIsSameAsShipping,
    getAddressData: (state, getters) => {
        return getters.convertAddressData;
    },
    convertAddressData: (state) => {
        const allowedIds = state.isLoggedIn ? state.customerAddressIds : [];
        let shippingAddress = mapAddress(state.shippingAddress, allowedIds);

        let billingAddress = state.billingIsSameAsShipping
          ? {...shippingAddress}
          : mapAddress(state.billingAddress, allowedIds);

        if (state.billingIsSameAsShipping) {
            billingAddress['saveInAddressBook'] = null;
        }

        return [billingAddress, shippingAddress];
    },
};

const mutations = {
    SET_BILLING_ADDRESS(state, billingAddress) {
        state.billingAddress = billingAddress;
        appLocalStorage.setItem('checkout-billing-address', state.billingAddress);
    },
    SET_SHIPPING_ADDRESS(state, shippingAddress) {
        state.shippingAddress = shippingAddress;
        appLocalStorage.setItem('checkout-shipping-address', state.shippingAddress);
    },
    SET_BILLING_IS_SAME_AS_SHIPPING(state, status) {
        appLocalStorage.setItem('checkout-billing-is-same-as-shipping', status);
        state.billingIsSameAsShipping = status;
    },
    SET_CUSTOMER_CONTEXT(state, payload) {
        const isLoggedIn = !!payload?.isLoggedIn;
        const customerAddressIds = (payload?.addressIds || [])
          .map(toCustomerAddressId)
          .filter(Boolean);

        state.isLoggedIn = isLoggedIn;
        state.customerAddressIds = customerAddressIds;

        const allowedIds = isLoggedIn ? customerAddressIds : [];
        state.shippingAddress = sanitizePersistedAddress(state.shippingAddress, allowedIds);
        state.billingAddress = sanitizePersistedAddress(state.billingAddress, allowedIds);
        appLocalStorage.setItem('checkout-shipping-address', state.shippingAddress);
        appLocalStorage.setItem('checkout-billing-address', state.billingAddress);
    },
};

const actions = {
    setBillingAddress({commit}, payload) {
        commit('SET_BILLING_ADDRESS', payload['address']);
    },
    setShippingAddress({commit}, payload) {
        commit('SET_SHIPPING_ADDRESS', payload['address']);
    },
    toggleBillingIsSameAsShipping({state, commit}) {
        commit('SET_BILLING_IS_SAME_AS_SHIPPING', ! state.billingIsSameAsShipping);
    },
};

const state = initialState();

const CheckoutAddress = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export default CheckoutAddress;
